import { isAxiosError, type AxiosError } from 'axios';
import { ZodError } from 'zod';

type Kind = 'network' | 'auth' | 'forbidden' | 'validation' | 'notFound' | 'conflict' | 'server' | 'unknown';

/** The single error shape the UI sees. `message` is always safe to show a user. */
export class AppError extends Error {
  readonly kind: Kind;
  readonly status: number | undefined;
  readonly traceId: string | undefined;

  constructor(kind: Kind, message: string, status?: number, traceId?: string) {
    super(message);
    this.name = 'AppError';
    this.kind = kind;
    this.status = status;
    this.traceId = traceId;
  }
}

const BY_STATUS: Record<number, [Kind, string]> = {
  401: ['auth', 'Your session has ended. Reload the page to continue.'],
  403: ['forbidden', 'Your role cannot do this. Switch role in the top bar.'],
  404: ['notFound', 'We could not find that item. It may have been removed.'],
  409: ['conflict', 'Someone else changed this first. Refresh to see the latest.'],
  422: ['validation', 'Some details are not valid. Check the form and try again.'],
};

/** Our API writes short, user-facing reasons for refusals ("You asked for this draft, so another analyst must
 * approve it"), conflicts and rejected input; anything else stays generic. */
function safeDetail(data: unknown, status: number): string | null {
  if (status !== 403 && status !== 409 && status !== 422) return null;
  const detail = typeof data === 'object' && data !== null && 'detail' in data ? data.detail : null;
  return typeof detail === 'string' && detail.length <= 200 ? detail : null;
}

function fromAxios(err: AxiosError): AppError {
  if (!err.response) return new AppError('network', 'Cannot reach the server. Check your connection.');
  const { status } = err.response;
  const traceId = err.response.headers['x-trace-id'] as string | undefined;
  const [kind, message] = BY_STATUS[status] ?? ['server', 'Something went wrong on our side.'];
  return new AppError(kind, safeDetail(err.response.data, status) ?? message, status, traceId);
}

export function toAppError(err: unknown): AppError {
  if (err instanceof AppError) return err;
  if (err instanceof ZodError) return new AppError('server', 'The server sent data in an unexpected shape.');
  if (isAxiosError(err)) return fromAxios(err);
  return new AppError('unknown', 'Something unexpected happened.');
}

export const isAppError = (value: unknown): value is AppError => value instanceof AppError;
